import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { CsvSchema } from 'cloud-shared-lib';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CuttingButt } from '../../../models/cutting/cutting-butt.model';
import { CuttingClientOptions, ImportCsvSaveModeEnum } from '../../../models/cutting/cutting-client.model';
import { CuttingPanelCsvInfo } from '../../../models/cutting/cutting-panel-csv.model';
import { CuttingPanel } from '../../../models/cutting/cutting-panel.model';
import { CuttingButtFormService } from '../../../services/cutting-butt-form.service';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import { CsvSchemaDetailDialogComponent } from '../../csv-schema-detail-dialog/csv-schema-detail-dialog.component';
import {
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
} from '../../material-base-butts-dialog/material-base-butts-dialog.component';
import { CuttingPanelListService } from '../cutting-panel-list.service';

export interface SwapingButt {
  thichness: number;
  butt: CuttingButt;
}

@Component({
  selector: 'csl-panels-import-csv-dialog',
  templateUrl: './import-from-csv-dialog.component.html',
  styleUrls: ['./import-from-csv-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ImportFromCsvDialogComponent implements OnInit, OnDestroy {
  materialBaseDialogRef: MatDialogRef<MaterialBaseButtsDialogComponent>;
  dataSource: MatTableDataSource<SwapingButt> = new MatTableDataSource();
  clientOptions: CuttingClientOptions;
  displayedColumns = ['oldButt', 'newButt'];
  buttsInColorList: CuttingButt[] = [];
  otherButts: CuttingButt[] = [];
  csvPanels: CuttingPanelCsvInfo[] = [];
  panels: CuttingPanel[] = [];
  selectedSchema: CsvSchema;
  csvSchemas: CsvSchema[];
  saveMode: ImportCsvSaveModeEnum = ImportCsvSaveModeEnum.CompleteAll;

  get csvSchemaId(): number {
    return this.cuttingPanelListService.onClientOptionsChanged.value.csvSchemaId;
  }

  unsubscribe$: Subject<void> = new Subject();

  @Input() matInMatBaseId = 0;
  @Input() materialThickness = 0;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ImportFromCsvDialogComponent>,
    public cuttingPanelListService: CuttingPanelListService,
    public buttFormService: CuttingButtFormService,
    private snackBar: MatSnackBar,
    private dialogsService: LegacyDialogsService
  ) {}

  ngOnInit(): void {
    this.getCsvSchemas();

    this.buttFormService
      .getMatchedButtsForClient(this.matInMatBaseId, this.materialThickness)
      .then((response: CuttingButt[]) => {
        this.buttsInColorList = response;
      });

    this.cuttingPanelListService.onClientOptionsChanged.subscribe(response => {
      this.clientOptions = response;
    });
  }

  deleteSchema(): void {
    this.dialogsService
      .confirm($localize`Удалить схему ${this.selectedSchema.name} ?`)
      .pipe(
        filter(res => res),
        switchMap(() => {
          this.toggleActive(this.csvSchemas[0]);
          return this.cuttingPanelListService.deleteCsvSchema(this.selectedSchema.id);
        }),
        tap(() => this.getCsvSchemas())
      )
      .subscribe();
  }

  openEditSchemaDialog(): void {
    const dialogData = { csvSchema: this.selectedSchema, isClient: true };
    this.dialogsService
      .component(CsvSchemaDetailDialogComponent, dialogData, 500)
      .pipe(
        filter(res => !!res),
        switchMap(result => {
          result.id = this.selectedSchema.id;
          return this.cuttingPanelListService.updateCsvSchema(result);
        }),
        tap(() => this.getCsvSchemas()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  openNewSchemaDialog(): void {
    const dialogData = { csvSchema: new CsvSchema(), isClient: false };

    this.dialogsService
      .component(CsvSchemaDetailDialogComponent, dialogData, 500)
      .pipe(
        filter(res => !!res),
        switchMap(result => this.cuttingPanelListService.addCsvSchema(result)),
        tap(res => {
          this.toggleActive(res);
          this.getCsvSchemas();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  toggleActive(item: CsvSchema): void {
    this.clientOptions.csvSchemaId = item.id;
    this.cuttingPanelListService.saveClientOptions(this.clientOptions);
  }

  saveAndClose(): void {
    if (this.dataSource.data.findIndex(sb => sb.butt.sign === '') !== -1) {
      this.snackBar.open($localize`Не для всех толщин сопоставлена облицовка`);
      return;
    }

    this.csvPanels.forEach(csvPanel => {
      const panel = new CuttingPanel();
      panel.position = csvPanel.position;
      panel.name = csvPanel.name;
      panel.height = csvPanel.length;
      panel.width = csvPanel.width;
      panel.count = csvPanel.count;
      panel.orientation = csvPanel.noOrientation === '';
      panel.remark = csvPanel.remark;

      if (csvPanel.thicknessL1 > 0) {
        const swappingButt = this.dataSource.data.find(sb => sb.thichness === csvPanel.thicknessL1);
        if (swappingButt) {
          panel.l1 = swappingButt.butt;
        }
      }

      if (csvPanel.thicknessL2 > 0) {
        const swappingButt = this.dataSource.data.find(sb => sb.thichness === csvPanel.thicknessL2);
        if (swappingButt) {
          panel.l2 = swappingButt.butt;
        }
      }

      if (csvPanel.thicknessW1 > 0) {
        const swappingButt = this.dataSource.data.find(sb => sb.thichness === csvPanel.thicknessW1);
        if (swappingButt) {
          panel.w1 = swappingButt.butt;
        }
      }

      if (csvPanel.thicknessW2 > 0) {
        const swappingButt = this.dataSource.data.find(sb => sb.thichness === csvPanel.thicknessW2);
        if (swappingButt) {
          panel.w2 = swappingButt.butt;
        }
      }

      this.panels.push(panel);
    });

    this.dialogRef.close({ result: this.panels, saveMode: this.saveMode });
  }

  openMaterialBaseDialog(filterByThickness: number): Promise<CuttingButt | undefined> {
    this.materialBaseDialogRef = this.dialog.open(MaterialBaseButtsDialogComponent, {
      panelClass: 'material-base-butt-list-dialog',
      disableClose: false,
      width: '1000px',
      data: {
        filterByThickness: filterByThickness,
        panelThickness: this.materialThickness,
      } as MaterialBaseEdgeDialogData,
    });

    return new Promise((resolve, reject) => {
      this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingButt | undefined) => {
        this.materialBaseDialogRef = null;
        resolve(result);
      }, reject);
    });
  }

  async setButtFromMatBase(item: SwapingButt, select: MatSelect): Promise<void> {
    const newButtFromBM = await this.openMaterialBaseDialog(item.thichness);

    if (newButtFromBM) {
      const nbindex = this.dataSource.data.indexOf(item);
      this.otherButts.push(newButtFromBM);
      this.dataSource.data[nbindex].butt = newButtFromBM;
      select.valueChange.emit(newButtFromBM);
    }
  }

  afterParsedFile(): void {
    const buttThichnessList: number[] = [];
    this.csvPanels.forEach(csvPanel => {
      if (csvPanel.thicknessL1 > 0 && buttThichnessList.indexOf(csvPanel.thicknessL1) === -1) {
        buttThichnessList.push(csvPanel.thicknessL1);
      }

      if (csvPanel.thicknessL2 > 0 && buttThichnessList.indexOf(csvPanel.thicknessL2) === -1) {
        buttThichnessList.push(csvPanel.thicknessL2);
      }

      if (csvPanel.thicknessW1 > 0 && buttThichnessList.indexOf(csvPanel.thicknessW1) === -1) {
        buttThichnessList.push(csvPanel.thicknessW1);
      }

      if (csvPanel.thicknessW2 > 0 && buttThichnessList.indexOf(csvPanel.thicknessW2) === -1) {
        buttThichnessList.push(csvPanel.thicknessW2);
      }
    });

    if (buttThichnessList.length > 0) {
      const butts: SwapingButt[] = [];
      buttThichnessList.forEach(buttThichness => {
        let linkButt: CuttingButt;
        const findButt = this.buttsInColorList.find(sb => Math.abs(sb.thickness - buttThichness) < 0.001);
        if (findButt) {
          linkButt = findButt;
        } else {
          linkButt = new CuttingButt();
        }
        butts.push({ thichness: buttThichness, butt: linkButt });
      });

      this.dataSource.data = butts;
    } else {
      this.saveAndClose();
    }
  }

  uploadFile(file: File): void {
    this.cuttingPanelListService
      .getPanelsFromCsv(file, this.selectedSchema.id)
      .then((data: CuttingPanelCsvInfo[]) => {
        this.csvPanels = data;
        const snackBarText = $localize`Загружена информация по ${this.csvPanels.length} панели(ям)`;
        this.snackBar.open(snackBarText, 'OK');

        this.afterParsedFile();
      })
      .catch(error => this.snackBar.open($localize`При загрузке данных из файла произошла ошибка`));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private getCsvSchemas(): void {
    this.cuttingPanelListService
      .getCsvSchemas()
      .pipe(
        tap(result => {
          this.csvSchemas = result;
          this.selectedSchema = this.csvSchemas.find(s => s.id === this.csvSchemaId) ?? this.csvSchemas[0];
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }
}
